import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { TitleSlide } from './slides/TitleSlide';
import { ProblemSlide } from './slides/ProblemSlide';
import { SolutionSlide } from './slides/SolutionSlide';
import { ProductSlide } from './slides/ProductSlide';
import { MarketSlide } from './slides/MarketSlide';
import { BusinessModelSlide } from './slides/BusinessModelSlide';
import { TractionSlide } from './slides/TractionSlide';
import { CompetitionSlide } from './slides/CompetitionSlide';
import { TeamSlide } from './slides/TeamSlide';
import { AskSlide } from './slides/AskSlide';
import { EmailGate } from './EmailGate';
const slides = [TitleSlide, ProblemSlide, SolutionSlide, ProductSlide, MarketSlide, BusinessModelSlide, TractionSlide, CompetitionSlide, TeamSlide, AskSlide];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showGate, setShowGate] = useState(false);
  useEffect(() => {
    const unlocked = localStorage.getItem('openfpga_email_unlocked') === 'true';
    setIsUnlocked(unlocked);
  }, []);
  const goToSlide = useCallback((index: number) => {
    if (index < 0 || index >= slides.length || index === currentSlide) return;
    // Gate check
    if (!isUnlocked && index > 0) {
      setShowGate(true);
      return;
    }
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide, isUnlocked]);
  const next = useCallback(() => {
    if (currentSlide < slides.length - 1) goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);
  const prev = useCallback(() => {
    if (currentSlide > 0) goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        next();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [next, prev]);
  const SlideComponent = slides[currentSlide];
  return <div className="w-full h-screen bg-[#0a0e1a] flex flex-col items-center justify-center circuit-grid relative overflow-hidden" data-id="element-45">
      <EmailGate isOpen={showGate} onUnlock={() => {
      setIsUnlocked(true);
      setShowGate(false);
      // Automatically advance to the next slide after unlock
      if (currentSlide === 0) {
        setDirection(1);
        setCurrentSlide(1);
      }
    }} data-id="element-46" />

      {/* Slide container */}
      <div className="slide-frame relative rounded-xl overflow-hidden border border-[rgba(255,255,255,0.06)] bg-[#0c1020] shadow-2xl" data-id="element-47">
        <AnimatePresence mode="wait" custom={direction} data-id="element-48">
          <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.35,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="absolute inset-0" data-id="element-49">
            <SlideComponent data-id="element-50" />
          </motion.div>
        </AnimatePresence>

        {/* Slide number */}
        <div className="absolute top-4 right-5 text-[11px] text-[#8899aa] font-mono z-10" data-id="element-51">
          {String(currentSlide + 1).padStart(2, '0')} /{' '}
          {String(slides.length).padStart(2, '0')}
        </div>
      </div>

      {/* Navigation controls */}
      <div className="mt-6 flex items-center gap-6" data-id="element-52">
        {/* Prev button */}
        <button onClick={prev} disabled={currentSlide === 0} className="w-9 h-9 rounded-full border border-[rgba(255,255,255,0.1)] flex items-center justify-center text-[#8899aa] hover:text-white hover:border-[rgba(255,255,255,0.25)] transition-colors disabled:opacity-20 disabled:cursor-not-allowed" aria-label="Previous slide" data-id="element-53">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-54" />
        </button>

        {/* Dot indicators */}
        <div className="flex items-center gap-2" data-id="element-55">
          {slides.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`transition-all duration-300 rounded-full ${index === currentSlide ? 'w-6 h-2 bg-[#00d4ff] shadow-[0_0_8px_rgba(0,212,255,0.4)]' : 'w-2 h-2 bg-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.3)]'}`} aria-label={`Go to slide ${index + 1}`} aria-current={index === currentSlide ? 'true' : undefined} data-id="element-56" />)}
        </div>

        {/* Next button */}
        <button onClick={next} disabled={currentSlide === slides.length - 1} className="w-9 h-9 rounded-full border border-[rgba(255,255,255,0.1)] flex items-center justify-center text-[#8899aa] hover:text-white hover:border-[rgba(255,255,255,0.25)] transition-colors disabled:opacity-20 disabled:cursor-not-allowed" aria-label="Next slide" data-id="element-57">
          <ChevronRightIcon className="w-4 h-4" data-id="element-58" />
        </button>
      </div>

      {/* Keyboard hint */}
      <p className="mt-3 text-[10px] text-[rgba(255,255,255,0.15)] tracking-wider" data-id="element-59">
        ← → arrow keys to navigate
      </p>
    </div>;
}